import ProfileSourceLine from "./ProfileSourceLine";
import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import colors from "../../assets/theme/colors";
import HandbookSourceListViewer from "./HandbookSourceListViewer";
import KnowledgeProfileItemList from "./KnowledgeProfileItemList";

const formatConfirmationDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const getProvenanceLines = (provenance) => {
  if (!provenance) return [];

  const lines = [];
  const supportsConfirmation = provenance.mode === "last_confirmed" || provenance.mode === "combined";

  if (supportsConfirmation && provenance.lastConfirmedAt) {
    const dateLabel = formatConfirmationDate(provenance.lastConfirmedAt);
    const confirmer = provenance.confirmedBy?.label;
    lines.push(`${confirmer ? `Confirmed by ${confirmer}` : "Last confirmed"} ${dateLabel}`);
  }

  return lines;
};

// A clickable "View sources" link replaces the old plain-text summary
// whenever caregiver-facing records are available (provenance.records, built
// by chapterProvenance.js). Facts with only a bare sourceCount (no named
// records to show) fall back to the same plain count line as before - there
// is nothing to list in a sheet for those.
const Provenance = ({ provenance, sx }) => {
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const lines = getProvenanceLines(provenance);
  const records = Array.isArray(provenance?.records) ? provenance.records : [];
  const hasRecords = records.length > 0;
  const hasBareCount = !hasRecords
    && (provenance?.mode === "source_count" || provenance?.mode === "combined")
    && provenance.sourceCount;

  if (!lines.length && !hasRecords && !hasBareCount) return null;

  return (
    <Stack spacing={0.25} sx={sx}>
      {hasRecords ? (
        <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
          {records.length > 1 ? (
            <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.78rem", lineHeight: 1.4 }}>
              {`Verified by ${records.length} records`}
            </Typography>
          ) : null}
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={(event) => {
              // entry.provenance can render inside an already-interactive
              // entry's own outer <button> (its sourceAction), so this must
              // never itself be a nested <button> (invalid DOM) and must stop
              // the click from also triggering the entry's own action.
              event.stopPropagation();
              setSourcesOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              setSourcesOpen(true);
            }}
            sx={{
              color: colors.semantic.sageDeep,
              fontSize: "0.78rem",
              fontWeight: 500,
              textDecoration: "underline",
              cursor: "pointer",
              "&:focus-visible": { outline: `2px solid ${colors.semantic.sageDeep}`, outlineOffset: 2 },
            }}
          >
            View sources
          </Box>
        </Stack>
      ) : hasBareCount ? (
        <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.78rem", lineHeight: 1.4 }}>
          {`Supported by ${provenance.sourceCount} care source${provenance.sourceCount === 1 ? "" : "s"}`}
        </Typography>
      ) : null}
      {lines.map((line) => (
        <Typography key={line} sx={{ color: colors.semantic.ink2, fontSize: "0.78rem", lineHeight: 1.4 }}>
          {line}
        </Typography>
      ))}
      <HandbookSourceListViewer open={sourcesOpen} records={records} onClose={() => setSourcesOpen(false)} />
    </Stack>
  );
};

const Briefing = ({ briefing, showProvenance = true }) => {
  const items = Array.isArray(briefing?.items) ? briefing.items : [];
  if (!items.length) return null;

  return (
    <>
      <Stack spacing={0.85} sx={{ mt: 1.15 }}>
        {items.map((item) => (
          <Box key={item.id} sx={{ display: "grid", gridTemplateColumns: "20px minmax(0, 1fr)", gap: 0.8, alignItems: "baseline" }}>
            <CheckRoundedIcon sx={{ fontSize: 19, color: colors.semantic.sageDeep }} />
            <Typography sx={{ color: colors.semantic.ink, fontSize: "0.96rem", lineHeight: 1.4 }}>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Stack>
      {showProvenance ? <Provenance provenance={briefing.provenance} sx={{ mt: 1.1 }} /> : null}
    </>
  );
};

// entry.sourceAction is optional and domain-agnostic here: this component only
// knows "there is an action, here is its label" - it has no idea what Edit or
// View source means. The domain-specific behavior lives entirely in the caller
// via onEntryAction, so this card stays reusable across every chapter.
const HandbookEntry = ({
  entry,
  onEntryAction,
  onEntryVisibilityAction,
  onEntryMarkOutdated,
}) => {
  const sourceAction = entry.sourceAction;
  const isExternalSourceAction = sourceAction?.ownership === "external_record" && Boolean(entry.sourcePresentation);
  const isInteractive = Boolean(sourceAction && onEntryAction && !isExternalSourceAction);
  const sourceRecords = Array.isArray(entry.provenance?.records) ? entry.provenance.records : [];

  const body = (
    <>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.6 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {entry.sourcePresentation?.contentLabel ? (
            <Typography sx={{
              mb: 0.25,
              color: colors.semantic.ink2,
              fontSize: "0.72rem",
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}>
              {entry.sourcePresentation.contentLabel}
            </Typography>
          ) : null}
          <Typography sx={{ color: colors.semantic.ink, fontSize: "0.98rem", fontWeight: 500, lineHeight: 1.35 }}>
            {entry.headline}
          </Typography>
          <Typography sx={{ mt: 0.2, color: colors.semantic.ink2, fontSize: "0.88rem", lineHeight: 1.5 }}>
            {entry.explanation}
          </Typography>
        </Box>
        {isInteractive ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.15, flexShrink: 0, mt: 0.25 }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: colors.semantic.sageDeep, lineHeight: 1 }}>
              {sourceAction.actionLabel}
            </Typography>
            <ChevronRightRoundedIcon aria-hidden="true" sx={{ fontSize: 16, color: colors.semantic.sageDeep }} />
          </Box>
        ) : null}
      </Box>
      {entry.responseGuidance ? (
        <Box sx={{ mt: 0.75, pl: 1, borderLeft: `3px solid ${colors.semantic.sageSoft}` }}>
          {entry.responseGuidance.label ? (
            <Typography sx={{ color: colors.semantic.ink, fontSize: "0.8rem", fontWeight: 500 }}>
              {entry.responseGuidance.label}
            </Typography>
          ) : null}
          <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.84rem", lineHeight: 1.45 }}>
            {entry.responseGuidance.text}
          </Typography>
        </Box>
      ) : null}
      {entry.sourcePresentation ? (
        <ProfileSourceLine
          presentation={{
            ...entry.sourcePresentation,
            ...(isExternalSourceAction && sourceAction?.actionLabel
              ? { actionLabel: sourceAction.actionLabel }
              : {}),
          }}
          records={sourceRecords}
          onAction={isExternalSourceAction && onEntryAction
            ? (event) => onEntryAction(entry, event.currentTarget)
            : undefined}
          onVisibilityAction={entry.knowledgeFactId && onEntryVisibilityAction
            ? () => onEntryVisibilityAction(entry)
            : undefined}
          onMarkOutdated={entry.knowledgeFactId && onEntryMarkOutdated
            ? () => onEntryMarkOutdated(entry)
            : undefined}
        />
      ) : (
        <Provenance provenance={entry.provenance} sx={{ mt: 0.65 }} />
      )}
    </>
  );

  if (!isInteractive || entry.sourcePresentation?.sourceId) {
    return <Box>{body}{isInteractive ? (
      <Button size="small" onClick={event => onEntryAction(entry, event.currentTarget)} sx={{p:0,textTransform:'none'}}>{sourceAction.actionLabel}</Button>
    ) : null}</Box>;
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={(event) => onEntryAction(entry, event.currentTarget)}
      aria-label={`${sourceAction.actionLabel}: ${entry.headline}`}
      sx={{
        width: "100%",
        textAlign: "left",
        background: "none",
        border: "none",
        p: 0,
        m: 0,
        font: "inherit",
        cursor: "pointer",
        borderRadius: "12px",
        "&:focus-visible": { outline: `2px solid ${colors.semantic.sageDeep}`, outlineOffset: 2 },
      }}
    >
      {body}
    </Box>
  );
};

const Handbook = ({
  handbook,
  onEntryAction,
  onEntryVisibilityAction,
  onEntryMarkOutdated,
}) => {
  const groups = Array.isArray(handbook?.groups) ? handbook.groups : [];

  return (
    <Box sx={{ borderTop: `1px solid ${colors.semantic.line}`, bgcolor: alpha(colors.semantic.card, 0.65) }}>
      <Stack spacing={2.2} sx={{ px: { xs: 1.5, sm: 2.1 }, py: 2 }}>
        {groups.map((group) => (
          <Stack key={group.id} spacing={1.1}>
            <Box>
              <Typography sx={{ color: colors.semantic.ink, fontSize: "0.96rem", fontWeight: 500 }}>
                {group.title}
              </Typography>
              {group.summary ? (
                <Typography sx={{ mt: 0.2, color: colors.semantic.ink2, fontSize: "0.84rem", lineHeight: 1.45 }}>
                  {group.summary}
                </Typography>
              ) : null}
            </Box>
            {(group.entries || []).map((entry) => (
              <HandbookEntry
                key={entry.id}
                entry={entry}
                onEntryAction={onEntryAction}
                onEntryVisibilityAction={onEntryVisibilityAction}
                onEntryMarkOutdated={onEntryMarkOutdated}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const LoadingPlaceholder = () => (
  <Stack spacing={0.7} sx={{ mt: 1 }} role="status" aria-label="Loading chapter">
    <Box aria-hidden="true" sx={{ height: 14, borderRadius: 999, width: "86%", bgcolor: alpha(colors.semantic.line, 1) }} />
    <Box aria-hidden="true" sx={{ height: 14, borderRadius: 999, width: "64%", bgcolor: alpha(colors.semantic.line, 1) }} />
    <Box aria-hidden="true" sx={{ height: 14, borderRadius: 999, width: "72%", bgcolor: alpha(colors.semantic.line, 1) }} />
  </Stack>
);

const AddCtaButton = ({ label, onClick }) => (
  <Button
    onClick={onClick}
    startIcon={<AddRoundedIcon />}
    size="small"
    sx={{
      px: 1.6,
      borderRadius: 999,
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.84rem",
      bgcolor: alpha(colors.semantic.sageDeep, 0.1),
      color: colors.semantic.sageDeep,
      "&:hover": { bgcolor: alpha(colors.semantic.sageDeep, 0.18) },
    }}
  >
    {label || "Add"}
  </Button>
);

const CaregiverHandbookCard = ({
  chapter,
  status = "ready",
  expanded,
  defaultExpanded = false,
  onExpandedChange,
  onEntryAction,
  onEntryVisibilityAction,
  onEntryMarkOutdated,
  onEntryDelete,
  onAddCta,
  addCtaLabel,
  presentationMode = "summary",
  showGroupHeadings = false,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = typeof expanded === "boolean";
  const isExpanded = isControlled ? expanded : internalExpanded;
  const briefingItems = Array.isArray(chapter?.briefing?.items) ? chapter.briefing.items : [];
  const handbookGroups = Array.isArray(chapter?.handbook?.groups) ? chapter.handbook.groups : [];
  const hasFactSourceLines = handbookGroups.some((group) => (
    (group.entries || []).some((entry) => Boolean(entry.sourcePresentation))
  ));
  const hasHandbook = handbookGroups.length > 0;
  const isEmpty = briefingItems.length === 0 && !hasHandbook;
  const isLoading = status === "loading";
  const usesProfileItems = presentationMode === "profileItems";

  if (!chapter) return null;

  const handleExpandedChange = () => {
    const nextExpanded = !isExpanded;
    if (!isControlled) setInternalExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded);
  };

  return (
    <Box aria-busy={isLoading} sx={{ border: `1px solid ${colors.semantic.line}`, borderRadius: "24px", bgcolor: colors.semantic.bg, overflow: "hidden" }}>
      <Box sx={{ px: { xs: 1.5, sm: 2.1 }, py: 1.8 }}>
        {chapter.title ? (
          <Typography sx={{ mb: 0.35, color: colors.semantic.ink2, fontSize: "0.78rem", fontWeight: 500 }}>
            {chapter.title}
          </Typography>
        ) : null}
        <Typography component="h2" sx={{ color: colors.semantic.ink, fontSize: { xs: "1.08rem", sm: "1.2rem" }, fontWeight: 500, lineHeight: 1.3 }}>
          {chapter.caregiverQuestion}
        </Typography>
        {chapter.description ? (
          <Typography sx={{ mt: 0.35, color: colors.semantic.ink2, fontSize: "0.86rem", lineHeight: 1.45 }}>
            {chapter.description}
          </Typography>
        ) : null}

        {isLoading ? (
          <LoadingPlaceholder />
        ) : isEmpty ? (
          <Box sx={{ mt: 0.9 }}>
            {chapter.emptyState?.description ? (
              <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.88rem", lineHeight: 1.45 }}>
                {chapter.emptyState.description}
              </Typography>
            ) : null}
            {onAddCta ? (
              <Box sx={{ mt: 1.1 }}>
                <AddCtaButton label={addCtaLabel} onClick={onAddCta} />
              </Box>
            ) : null}
          </Box>
        ) : usesProfileItems ? (
          <Box sx={{ mt: 1.2 }}>
            <KnowledgeProfileItemList
              handbook={chapter.handbook}
              onEntryAction={onEntryAction}
              onEntryVisibilityAction={onEntryVisibilityAction}
              onEntryMarkOutdated={onEntryMarkOutdated}
              onEntryDelete={onEntryDelete}
              onAddCta={onAddCta}
              addCtaLabel={addCtaLabel}
              showGroupHeadings={showGroupHeadings}
            />
          </Box>
        ) : (
          <>
            <Briefing briefing={chapter.briefing} showProvenance={!hasFactSourceLines} />
            {onAddCta ? (
              <Box sx={{ mt: 1.15 }}>
                <AddCtaButton label="Add another" onClick={onAddCta} />
              </Box>
            ) : null}
          </>
        )}

        {hasHandbook && !usesProfileItems ? (
          <Button
            onClick={handleExpandedChange}
            aria-expanded={isExpanded}
            endIcon={isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            sx={{ mt: 1.15, px: 0.25, color: colors.semantic.sageDeep, textTransform: "none", fontWeight: 500 }}
          >
            {isExpanded ? "Hide details" : "View details"}
          </Button>
        ) : null}
      </Box>

      {hasHandbook && !usesProfileItems ? (
        <Collapse in={isExpanded} unmountOnExit>
          <Handbook
            handbook={chapter.handbook}
            onEntryAction={onEntryAction}
            onEntryVisibilityAction={onEntryVisibilityAction}
            onEntryMarkOutdated={onEntryMarkOutdated}
          />
        </Collapse>
      ) : null}
    </Box>
  );
};

export default CaregiverHandbookCard;
